import {type Route, type RouteParams} from './types'

export class Router<T extends object> {
  routes: [string, Route][] = []
  constructor(
    description: Record<keyof T, string | string[]>,
    transformParams: (
      screen: string,
      params: Record<string, unknown>,
    ) => Record<string, unknown> = (_screen, params) => params,
  ) {
    for (const [screen, pattern] of Object.entries(description) as [
      string,
      string | string[],
    ][]) {
      if (typeof pattern === 'string') {
        this.routes.push([
          screen,
          withParamTransform(createRoute(pattern), screen, transformParams),
        ])
      } else {
        pattern.forEach(subPattern => {
          this.routes.push([
            screen,
            withParamTransform(
              createRoute(subPattern),
              screen,
              transformParams,
            ),
          ])
        })
      }
    }
  }

  matchName(name: keyof T | (string & {})): Route | undefined {
    for (const [screenName, route] of this.routes) {
      if (screenName === name) {
        return route
      }
    }
  }

  matchPath(path: string): [string, RouteParams] {
    let name = 'NotFound'
    let params: RouteParams = {}
    for (const [screenName, route] of this.routes) {
      const res = route.match(path)
      if (res) {
        name = screenName
        params = res.params
        break
      }
    }
    return [name, params]
  }
}

function withParamTransform(
  route: Route,
  screen: string,
  transform: (
    screen: string,
    params: Record<string, unknown>,
  ) => Record<string, unknown>,
): Route {
  return {
    match(path) {
      const result = route.match(path)
      return result
        ? {params: transform(screen, result.params) as RouteParams}
        : undefined
    },
    build(params = {}) {
      return route.build(transform(screen, params))
    },
  }
}

function createRoute(pattern: string): Route {
  const pathParamNames: Set<string> = new Set()
  let matcherReInternal = pattern.replace(/:([\w]+)/g, (_m, name) => {
    pathParamNames.add(name)
    return `(?<${name}>[^/]+)`
  })
  const matcherRe = new RegExp(`^${matcherReInternal}([?]|$)`, 'i')
  return {
    match(path) {
      const {pathname, searchParams} = new URL(path, 'http://throwaway.com')
      const addedParams = Object.fromEntries(searchParams.entries())

      const res = matcherRe.exec(pathname)
      if (res) {
        return {params: Object.assign(addedParams, res.groups || {})}
      }
      return undefined
    },
    build(params = {}) {
      const str = pattern.replace(/:([\w]+)/g, (_m, name) =>
        routeParamToString(params[encodeURIComponent(name)] || 'undefined'),
      )

      let hasQp = false
      const qp = new URLSearchParams()
      for (const paramName in params) {
        if (!pathParamNames.has(paramName)) {
          qp.set(paramName, routeParamToString(params[paramName]))
          hasQp = true
        }
      }

      return str + (hasQp ? `?${qp.toString()}` : '')
    },
  }
}

function routeParamToString(value: unknown): string {
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }
  return JSON.stringify(value) ?? ''
}
